// Reglages personnels · Notifications (08/10) : la section native de Dust (fenetre « Reglages
// personnels » du menu du membre) montre ce qui marche vraiment sur la pile astraly. Novu y est
// inerte (front/lib/compta/courriels_coupes.ts) : la fenetre dans l'application, les sons, Slack et
// l'interrupteur « Email » de Novu ne font rien, et la preference Novu ne se charge jamais (la
// section tournait sans fin). Les courriels partent par notre SMTP (notifications_courriel_envoi.ts)
// et lisent trois metadonnees du membre, de portee globale, que cette section ecrit :
// - « courriels_desactives » : « true » coupe tous les courriels de notification (mentions,
//   messages, resultats des taches planifiees) ;
// - « conversation_notify_condition » (amont) : toute l'activite, mentions seulement ou rien, pour
//   les conversations (filtre amont des participants) ;
// - « email_notification_preferences » (amont) : le delai minimal entre deux courriels d'une meme
//   conversation (fenetreDeRelance), 30 minutes sans reglage.
// Ce module est PUR : valeurs lues, libelles, changements a enregistrer. Il n'importe pas
// notifications_courriel.ts (contenus des courriels, cote serveur) : la cle et la lecture de
// « courriels_desactives » y sont reprises et le test verifie qu'elles concordent.
import type {
  NotificationCondition,
  NotificationPreferencesDelay,
} from "@app/types/notification_preferences";
import {
  CONVERSATION_NOTIFICATION_METADATA_KEYS,
  DEFAULT_NOTIFICATION_CONDITION,
  isNotificationCondition,
  isNotificationPreferencesDelay,
  makeNotificationPreferencesUserMetadata,
  NOTIFICATION_CONDITION_OPTIONS,
  NOTIFICATION_DELAY_OPTIONS,
} from "@app/types/notification_preferences";

/**
 * Vrai sur la pile astraly : la section Notifications des reglages personnels est celle des
 * courriels (SectionNotificationsCourriel) et non celle de Novu.
 */
/**
 * Electron (08/10) : l'app pose « courriels_desactives » a chaque visite (revendication « courriels »
 * du jeton, fork #304) depuis ses propres Reglages ; l'interrupteur du chat y serait ecrase. Pirouette
 * et Boosts n'envoient pas cette revendication : l'interrupteur du chat y fait foi.
 */
export function interrupteurCourrielsVisible(
  marque: string | undefined = process.env.NEXT_PUBLIC_MARQUE
): boolean {
  return marque !== "electron";
}

export function notificationsParCourriel(): boolean {
  return true;
}

/** Le delai affiche quand le membre n'a rien regle : celui qu'applique l'envoi (FENETRE_DEFAUT_MS). */
export const DELAI_PAR_DEFAUT: NotificationPreferencesDelay = "30_minutes";

export const CLES_REGLAGES_NOTIFICATIONS = {
  desactives: "courriels_desactives",
  condition: CONVERSATION_NOTIFICATION_METADATA_KEYS.notifyCondition,
  delai: makeNotificationPreferencesUserMetadata("email"),
} as const;

export interface ReglagesNotifications {
  courrielsActifs: boolean;
  condition: NotificationCondition;
  delai: NotificationPreferencesDelay;
}

export function lireReglagesNotifications({
  desactives,
  condition,
  delai,
}: {
  desactives: string | null | undefined;
  condition: string | null | undefined;
  delai: string | null | undefined;
}): ReglagesNotifications {
  return {
    courrielsActifs: desactives !== "true",
    condition: isNotificationCondition(condition)
      ? condition
      : DEFAULT_NOTIFICATION_CONDITION,
    delai: isNotificationPreferencesDelay(delai) ? delai : DELAI_PAR_DEFAUT,
  };
}

/** Les metadonnees a ecrire pour passer de `initial` a `courant` (seulement ce qui a change). */
export function changementsAEnregistrer(
  initial: ReglagesNotifications,
  courant: ReglagesNotifications
): { key: string; value: string }[] {
  const changements: { key: string; value: string }[] = [];
  if (initial.courrielsActifs !== courant.courrielsActifs) {
    changements.push({
      key: CLES_REGLAGES_NOTIFICATIONS.desactives,
      value: courant.courrielsActifs ? "false" : "true",
    });
  }
  if (initial.condition !== courant.condition) {
    changements.push({
      key: CLES_REGLAGES_NOTIFICATIONS.condition,
      value: courant.condition,
    });
  }
  if (initial.delai !== courant.delai) {
    changements.push({
      key: CLES_REGLAGES_NOTIFICATIONS.delai,
      value: courant.delai,
    });
  }
  return changements;
}

/** Cles anglaises, traduites par t() (front/lib/compta/i18n.ts). */
export const LIBELLES_CONDITION: Record<NotificationCondition, string> = {
  all_messages: "All activity",
  only_mentions: "Mentions only",
  never: "Nothing",
};

export const LIBELLES_DELAI: Record<NotificationPreferencesDelay, string> = {
  "5_minutes": "Every 5 minutes",
  "15_minutes": "Every 15 minutes",
  "30_minutes": "Every 30 minutes",
  "1_hour": "Every hour",
  daily: "Once a day",
  weekly: "Once a week",
};

export const OPTIONS_CONDITION = NOTIFICATION_CONDITION_OPTIONS;
export const OPTIONS_DELAI = NOTIFICATION_DELAY_OPTIONS;

/** Toutes les cles de la section, pour le test du dictionnaire. */
export const CLES_TEXTES_NOTIFICATIONS = [
  "Notifications",
  "Choose when you receive an email",
  "Notifications are sent by email. In-app popups and sounds are not available.",
  "Email notifications",
  "Mentions, replies in your conversations and results of your scheduled tasks",
  "Notify me about",
  "In your conversations. A conversation where you are the only member always notifies you, unless you choose Nothing.",
  "Email frequency",
  "At most one email per conversation",
  "Scheduled task results are emailed after each run, whatever you choose above.",
  "Save",
  "Notification preferences saved",
  "Error updating notification preferences",
  ...Object.values(LIBELLES_CONDITION),
  ...Object.values(LIBELLES_DELAI),
] as const;
